import { Tooltip, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import {
  BuildingMultipleRegular,
  GridRegular,
  PeopleRegular,
  ReceiptRegular,
  SettingsRegular,
} from '@fluentui/react-icons';
import type { FluentIcon } from '@fluentui/react-icons';
import { NavLink } from 'react-router-dom';

const useStyles = makeStyles({
  rail: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    paddingTop: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground4,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    transitionProperty: 'width',
    transitionDuration: tokens.durationNormal,
  },
  expanded: {
    width: '220px',
  },
  collapsed: {
    width: '48px',
  },
  link: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    height: '40px',
    paddingLeft: '14px',
    paddingRight: tokens.spacingHorizontalM,
    color: tokens.colorNeutralForeground2,
    textDecoration: 'none',
    borderLeft: `3px solid transparent`,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground4Hover,
      color: tokens.colorNeutralForeground1,
    },
  },
  linkActive: {
    color: tokens.colorNeutralForeground1,
    backgroundColor: tokens.colorNeutralBackground4Selected,
    borderLeftColor: tokens.colorBrandForeground1,
  },
  icon: {
    fontSize: '20px',
    flexShrink: 0,
  },
  label: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

interface RailItem {
  to: string;
  label: string;
  icon: FluentIcon;
  /** NavLink `end` matching — the dashboard root must match exactly. */
  end: boolean;
}

const ITEMS: readonly RailItem[] = [
  { to: '/', label: 'Dashboard', icon: GridRegular, end: true },
  { to: '/tenants', label: 'Tenants', icon: BuildingMultipleRegular, end: false },
  { to: '/plans', label: 'Plans', icon: ReceiptRegular, end: false },
  { to: '/users', label: 'Users', icon: PeopleRegular, end: false },
  { to: '/settings', label: 'Settings', icon: SettingsRegular, end: false },
];

interface ServiceRailProps {
  collapsed: boolean;
}

/**
 * Dark, collapsible left service rail (portal.azure.com style). Each item is a
 * {@link NavLink} that highlights on the active route. When collapsed only the
 * icons show, with the label surfaced via tooltip.
 */
export function ServiceRail({ collapsed }: ServiceRailProps): JSX.Element {
  const styles = useStyles();

  return (
    <nav
      aria-label="Platform services"
      className={mergeClasses(styles.rail, collapsed ? styles.collapsed : styles.expanded)}
    >
      {ITEMS.map(({ to, label, icon: Icon, end }) => {
        const link = (
          <NavLink
            to={to}
            end={end}
            className={({ isActive }) => mergeClasses(styles.link, isActive && styles.linkActive)}
          >
            <Icon className={styles.icon} />
            {!collapsed && <span className={styles.label}>{label}</span>}
          </NavLink>
        );

        // When collapsed the label is hidden, so surface it via tooltip for a11y.
        return collapsed ? (
          <Tooltip key={to} content={label} relationship="label" positioning="after">
            {link}
          </Tooltip>
        ) : (
          <span key={to}>{link}</span>
        );
      })}
    </nav>
  );
}
